import { ConfirmDialog } from "@/components/ui/ConfirmDialog"
import { companyEditConfirmPresentation } from "@/features/company/constants"
import { AddUserModal } from "@/features/company/components/AddUserModal"
import { LibertyInternalNotes } from "@/features/company/components/LibertyInternalNotes"
import { UserManagementEditEmbeddedModal } from "@/features/user-management/components/UserManagementEditEmbeddedModal"
import { defaultTransferFormValues } from "../utils/transferFormUtils"
import { usesTransferWizardCardLayout } from "../utils/transferPropertyCompanyWizard"
import type { TransferWizardFormValues } from "../types"
import { StaffSelectionModal } from "./StaffSelectionModal"
import type { TransferProcessDialogsProps } from "../types"

/** Modals, confirm dialogs, and internal notes below the wizard card. */
export function TransferProcessDialogs({ ctx }: TransferProcessDialogsProps) {
  const {
    process_id_notes,
    transferNotesRefreshKey,
    fragment,
    addEmployeeModalOpen,
    addEmployeeModalCtx,
    closeAddEmployeeModal,
    loadAddedStaffs,
    bumpTransferLogNotesRefresh,
    userEditModalUserId,
    setUserEditModalUserId,
    staffSelectionOpen,
    setStaffSelectionOpen,
    addedstaffList,
    transferstaffList,
    onPrimaryContactSave,
    confirmPaymentClone,
    setConfirmPaymentClone,
    propertyData,
    setValue,
    maybeInactiveListed,
    loadNewStaffs,
    applySetPropertyInfo,
    confirmInactiveListed,
    setConfirmInactiveListed,
    flowConfirm,
    handleFlowConfirmPrimary,
    handleFlowConfirmSecondary,
  } = ctx

  const useNotesCardLayout = usesTransferWizardCardLayout(fragment)

  return (
    <>
      {process_id_notes ? (
        <div
          data-testid="transfer-process-internal-notes-section"
          className="mt-10 min-w-0 max-w-full border-t border-[#e5e5e5] pt-6"
        >
          <LibertyInternalNotes
            fromPage="TRANSFER_LOG"
            entityId={process_id_notes}
            canEdit={true}
            refreshKey={transferNotesRefreshKey}
            embedded={!useNotesCardLayout}
          />
        </div>
      ) : null}

      {addEmployeeModalOpen && addEmployeeModalCtx ? (
        <div data-testid="transfer-process-add-user-modal-host">
          <AddUserModal
            isOpen
            onClose={closeAddEmployeeModal}
            companyId={addEmployeeModalCtx.companyId}
            variant={addEmployeeModalCtx.kind === "propertyStaff" ? "propertyStaff" : "company"}
            propertyId={addEmployeeModalCtx.kind === "propertyStaff" ? addEmployeeModalCtx.propertyId : undefined}
            transferNotesId={addEmployeeModalCtx.transferNotesId}
            onSuccess={() => {
              void loadAddedStaffs()
              bumpTransferLogNotesRefresh()
            }}
          />
        </div>
      ) : null}

      <UserManagementEditEmbeddedModal
        isOpen={userEditModalUserId != null}
        userId={userEditModalUserId}
        onClose={() => setUserEditModalUserId(null)}
      />

      <StaffSelectionModal
        isOpen={staffSelectionOpen}
        addedstaffList={addedstaffList}
        transferstaffList={transferstaffList}
        onClose={() => setStaffSelectionOpen(false)}
        onSave={(p) => void onPrimaryContactSave(p)}
      />

      <ConfirmDialog
        isOpen={confirmPaymentClone.open}
        title="Apply payment information?"
        message={
          "Selected New Company has 'apply payment information to all property payments' setting ON. Do you want to apply its payment information to the property?\n\nPlease confirm that you want to proceed."
        }
        messageLayout="listingConfirm"
        {...companyEditConfirmPresentation}
        primaryLabel="Confirm"
        overlayDataTestId="transfer-process-confirm-payment-clone-dialog"
        primaryButtonDataTestId="transfer-process-confirm-payment-clone-confirm-button"
        secondaryButtonDataTestId="transfer-process-confirm-payment-clone-cancel-button"
        closeButtonDataTestId="transfer-process-confirm-payment-clone-close-button"
        onPrimary={() => {
          const response = confirmPaymentClone.companyPayload
          setConfirmPaymentClone({ open: false, companyPayload: null })
          if (!response || !propertyData) return
          const propData = { ...response } as Record<string, unknown>
          delete propData.listed
          propData.property_name = propertyData.property_name
          propData.company_name = propertyData.company_name
          if (!response.payment_address && !response.payment_city) {
            propData.payment_address = response.company_address
            propData.payment_city = response.company_city
            propData.payment_state = response.company_state
            propData.payment_zipcode = response.company_zipcode
          }
          propData.verify = response.verify
          Object.entries(propData).forEach(([k, v]) => {
            if (k in defaultTransferFormValues() && k !== "newpropertyinfo") {
              setValue(k as keyof TransferWizardFormValues, String(v ?? "") as never)
            }
          })
          void maybeInactiveListed()
          if (fragment === "transfer_property") void loadNewStaffs()
        }}
        secondaryLabel="Cancel"
        onSecondary={() => {
          setConfirmPaymentClone({ open: false, companyPayload: null })
          applySetPropertyInfo()
          void maybeInactiveListed()
          if (fragment === "transfer_property") void loadNewStaffs()
        }}
        onDismiss={() => setConfirmPaymentClone({ open: false, companyPayload: null })}
      />

      <ConfirmDialog
        isOpen={confirmInactiveListed}
        title="Inactive property"
        message={
          "Current Property is Inactive, Please Update to Active.\n\nPlease confirm that you want to proceed."
        }
        messageLayout="listingConfirm"
        {...companyEditConfirmPresentation}
        primaryLabel="OK"
        overlayDataTestId="transfer-process-confirm-inactive-listed-dialog"
        primaryButtonDataTestId="transfer-process-confirm-inactive-listed-ok-button"
        secondaryButtonDataTestId="transfer-process-confirm-inactive-listed-cancel-button"
        closeButtonDataTestId="transfer-process-confirm-inactive-listed-close-button"
        onPrimary={() => {
          setValue("listed", "1")
          setConfirmInactiveListed(false)
        }}
        secondaryLabel="Cancel"
        onSecondary={() => setConfirmInactiveListed(false)}
        onDismiss={() => setConfirmInactiveListed(false)}
      />

      <ConfirmDialog
        isOpen={flowConfirm.open}
        title={flowConfirm.title}
        message={flowConfirm.message}
        messageLayout={flowConfirm.messageLayout}
        {...companyEditConfirmPresentation}
        primaryLabel={flowConfirm.primaryLabel}
        onPrimary={handleFlowConfirmPrimary}
        secondaryLabel={flowConfirm.secondaryLabel}
        onSecondary={handleFlowConfirmSecondary}
        onDismiss={handleFlowConfirmSecondary}
        presentation={flowConfirm.presentation}
        primaryFirst={flowConfirm.primaryFirst}
        overlayDataTestId="transfer-process-flow-confirm-dialog"
        primaryButtonDataTestId="transfer-process-flow-confirm-primary-button"
        secondaryButtonDataTestId="transfer-process-flow-confirm-secondary-button"
        closeButtonDataTestId="transfer-process-flow-confirm-close-button"
      />
    </>
  )
}
